import {
  fieldMappingSchema,
  fieldMappingSummarySchema,
  fieldMappingUsageSchema,
  fieldValueMappingSchema,
  integrationInstanceSchema,
  jiraFieldValueSchema,
  loopFieldValueSchema,
} from '@tempo-io/work-integration-dto';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import type {
  CreateInstanceFieldMappingRequest,
  CreateWorkIntegrationInstanceRequest,
  FieldMappingSummary,
  FieldMappingUsage,
  FieldValueMapping,
  InstanceFieldMapping,
  JiraFieldValue,
  LoopFieldValue,
  SaveFieldValueMappingsRequest,
  UpdateInstanceFieldMappingRequest,
  WorkIntegration_Keys,
  WorkIntegrationInstance,
} from '@/common/api/types/workIntegrations';

const createInstance = async (
  payload: CreateWorkIntegrationInstanceRequest,
): Promise<WorkIntegrationInstance> => {
  const response = await tempoApi()
    .post('api/v1/work-integrations/instances', {
      json: payload,
    })
    .json();
  return integrationInstanceSchema.parse(response);
};

const getInstances = async (
  sourceSystemType?: WorkIntegration_Keys,
): Promise<WorkIntegrationInstance[]> => {
  const searchParams = new URLSearchParams();
  if (sourceSystemType) {
    searchParams.append('sourceSystemType', sourceSystemType);
  }
  const url = `api/v1/work-integrations/instances${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const response = await tempoApi().get(url).json();
  return z.array(integrationInstanceSchema).parse(response);
};

const deleteInstance = async (id: number): Promise<void> => {
  await tempoApi().delete(`api/v1/work-integrations/instances/${id}`);
};

const getInstanceFieldMappings = async (
  instanceId: number,
  projectId?: string,
): Promise<InstanceFieldMapping[]> => {
  const searchParams = new URLSearchParams();
  if (projectId) {
    searchParams.append('projectId', projectId);
  }
  const query = searchParams.toString();
  const url = `api/v1/work-integrations/instances/${instanceId}/field-mappings${query ? `?${query}` : ''}`;
  const response = await tempoApi().get(url).json();
  return z.array(fieldMappingSchema).parse(response);
};

const refreshInstanceFieldMappingAvailability = async (
  instanceId: number,
  ignoreDebounce = false,
): Promise<void> => {
  const query = ignoreDebounce ? '?ignoreDebounce=true' : '';
  await tempoApi().post(
    `api/v1/work-integrations/instances/${instanceId}/field-mappings/refresh-availability${query}`,
  );
};

const createInstanceFieldMapping = async (
  instanceId: number,
  payload: CreateInstanceFieldMappingRequest,
): Promise<InstanceFieldMapping> => {
  const response = await tempoApi()
    .post(`api/v1/work-integrations/instances/${instanceId}/field-mappings`, { json: payload })
    .json();
  return fieldMappingSchema.parse(response);
};

const deleteInstanceFieldMapping = async (instanceId: number, mappingId: number): Promise<void> => {
  await tempoApi().delete(
    `api/v1/work-integrations/instances/${instanceId}/field-mappings/${mappingId}`,
  );
};

const updateInstanceFieldMapping = async (
  instanceId: number,
  mappingId: number,
  payload: UpdateInstanceFieldMappingRequest,
): Promise<InstanceFieldMapping> => {
  const response = await tempoApi()
    .put(`api/v1/work-integrations/instances/${instanceId}/field-mappings/${mappingId}`, {
      json: payload,
    })
    .json();
  return fieldMappingSchema.parse(response);
};

const getInstanceFieldMappingsUsage = async (instanceId: number): Promise<FieldMappingUsage[]> => {
  const response = await tempoApi()
    .get(`api/v1/work-integrations/instances/${instanceId}/field-mappings/usage`)
    .json();
  return z.array(fieldMappingUsageSchema).parse(response);
};

const getFieldValueMappings = async (
  instanceFieldMappingId: number,
): Promise<FieldValueMapping[]> => {
  const response = await tempoApi()
    .get(
      `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/value-mappings`,
    )
    .json();
  return z.array(fieldValueMappingSchema).parse(response);
};

const getLoopFieldValues = async (instanceFieldMappingId: number): Promise<LoopFieldValue[]> => {
  const response = await tempoApi()
    .get(`api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/loop-values`)
    .json();
  return z.array(loopFieldValueSchema).parse(response);
};

const getJiraFieldValues = async (
  instanceFieldMappingId: number,
): Promise<JiraFieldValue[] | null> => {
  const response = await tempoApi().get(
    `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/jira-values`,
    { timeout: 60_000 },
  );
  if (response.status === 202) return null;
  return z.array(jiraFieldValueSchema).parse(await response.json());
};

const retryJiraFieldValueDiscovery = async (instanceFieldMappingId: number): Promise<void> => {
  await tempoApi().post(
    `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/jira-values/retry`,
  );
};

const saveFieldValueMappings = async (
  instanceFieldMappingId: number,
  payload: SaveFieldValueMappingsRequest,
): Promise<FieldValueMapping[]> => {
  const response = await tempoApi()
    .put(
      `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/value-mappings`,
      { json: payload },
    )
    .json();
  return z.array(fieldValueMappingSchema).parse(response);
};

const getFieldMappingSummary = async (
  instanceFieldMappingId: number,
): Promise<FieldMappingSummary> => {
  const response = await tempoApi()
    .get(`api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/summary`)
    .json();
  return fieldMappingSummarySchema.parse(response);
};

export const IntegrationInstancesAPI = {
  createInstance,
  getInstances,
  deleteInstance,
  getInstanceFieldMappings,
  refreshInstanceFieldMappingAvailability,
  createInstanceFieldMapping,
  deleteInstanceFieldMapping,
  updateInstanceFieldMapping,
  getInstanceFieldMappingsUsage,
  getFieldValueMappings,
  getLoopFieldValues,
  getJiraFieldValues,
  retryJiraFieldValueDiscovery,
  saveFieldValueMappings,
  getFieldMappingSummary,
} as const;
